/**
 * SafeStorage - A wrapper around localStorage and sessionStorage that never throws
 *
 * Features:
 * - All operations are wrapped in try/catch
 * - Automatically falls back to sessionStorage if localStorage is unavailable
 * - Returns null on errors instead of throwing
 */

type StorageType = 'localStorage' | 'sessionStorage';

class SafeStorage {
  private primaryStorage: Storage | null = null;
  private activeStorageType: StorageType | null = null;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Try localStorage first
    try {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      this.primaryStorage = window.localStorage;
      this.activeStorageType = 'localStorage';
    } catch (e) {
      // localStorage failed, try sessionStorage
      try {
        const testKey = '__storage_test__';
        window.sessionStorage.setItem(testKey, 'test');
        window.sessionStorage.removeItem(testKey);
        this.primaryStorage = window.sessionStorage;
        this.activeStorageType = 'sessionStorage';
      } catch (e) {
        // Both storage types failed, will operate in no-op mode
        this.primaryStorage = null;
        this.activeStorageType = null;
      }
    }
  }

  /**
   * Get an item from storage
   * @returns The stored value or null if not found or error occurred
   */
  getItem(key: string): string | null {
    if (!this.primaryStorage) {
      return null;
    }

    try {
      return this.primaryStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /**
   * Set an item in storage
   * @returns true if successful, false otherwise
   */
  setItem(key: string, value: string): boolean {
    if (!this.primaryStorage) {
      return false;
    }

    try {
      this.primaryStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Remove an item from storage
   * @returns true if successful, false otherwise
   */
  removeItem(key: string): boolean {
    if (!this.primaryStorage) {
      return false;
    }

    try {
      this.primaryStorage.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Clear all items from storage
   * @returns true if successful, false otherwise
   */
  clear(): boolean {
    if (!this.primaryStorage) {
      return false;
    }

    try {
      this.primaryStorage.clear();
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get the type of storage currently being used
   */
  getStorageType(): StorageType | null {
    return this.activeStorageType;
  }

  /**
   * Check if storage is available
   */
  isAvailable(): boolean {
    return this.primaryStorage !== null;
  }
}

// Export a singleton instance
const safeStorage = new SafeStorage();
export default safeStorage;
